/**
 * Whether a line item's dimensions are shippable.
 *
 * Split from lineItemsErrorMessages, which also covers description, weight,
 * temperature and handling unit. These are the rules about physical size — the
 * per-axis caps, the overall too-large check, and the liftgate long-thin
 * exception for freight that is long but light enough to be hand-offloaded.
 */

import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { ShipmentLimits } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentLimits";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";

export function errorMessageForDimensionValue(
  value: number | undefined,
  adjective: string,
  limits: ShipmentLimits,
): ErrorMessageType {
  if (value === undefined) {
    return "Required";
  }

  if (value === 0) {
    return "Should not be zero";
  }

  if (value < 0) {
    return "Should not be negative";
  }

  // The maximum-length limit doubles as the cap for width too — both are bounded by the truck
  // length. Per-axis caps (e.g. height) are checked elsewhere.
  if (value > limits.lineItem.maxLengthInches) {
    return "Too " + adjective;
  }

  return undefined;
}

export function errorMessageForHeightValue(
  value: number | undefined,
  limits: ShipmentLimits,
): ErrorMessageType {
  const baseError = errorMessageForDimensionValue(value, "tall", limits);
  if (baseError !== undefined) {
    return baseError;
  }

  if (value !== undefined && value > limits.lineItem.maxHeightInches) {
    return "Too tall";
  }

  return undefined;
}

// A long, thin, light item is hand-offloaded ("hand-bombed") rather than ridden up the liftgate
// platform, so it is exempt from both the liftgate dimension cap and the liftgate weight cap. The
// exception is universal across carriers because the freight never touches the liftgate. All three
// axes AND the weight must be present and qualifying — a partially-entered line item never
// qualifies, so we never suppress a real liftgate error mid-entry.
export function qualifiesForLiftGateLongThinException(
  li: AddLineItemType,
  limits: ShipmentLimits,
): boolean {
  const { length, width, height, weightPerHandlingUnit } = li;
  if (
    length === undefined ||
    width === undefined ||
    height === undefined ||
    weightPerHandlingUnit === undefined
  ) {
    return false;
  }
  const longest = Math.max(length, width, height);
  const shortest = Math.min(length, width, height);
  const secondLargest = length + width + height - longest - shortest;
  const exception = limits.liftgate.longThinException;
  return (
    longest <= exception.maxLengthInches &&
    secondLargest <= exception.maxSecondAxisInches &&
    weightPerHandlingUnit <= exception.maxWeightPerHandlingUnitPounds
  );
}

function errorMessageForSingleDimension(
  value: number | undefined,
  adjective: string,
  pickupLocation: AddLocationLocationType,
  deliveryLocation: AddLocationLocationType,
  limits: ShipmentLimits,
  isLongThinExempt: boolean,
): ErrorMessageType {
  const baseError = errorMessageForDimensionValue(value, adjective, limits);
  if (baseError !== undefined) {
    return baseError;
  }

  // Lift gate validation
  const pickupRequiresLiftGate = pickupLocation.accessorials.includes(
    "LOGISTICS_LIFT_GATE_REQUIRED",
  );
  const deliveryRequiresLiftGate = deliveryLocation.accessorials.includes(
    "LOGISTICS_LIFT_GATE_REQUIRED",
  );
  const requiresLiftGate = pickupRequiresLiftGate || deliveryRequiresLiftGate;

  if (
    requiresLiftGate &&
    !isLongThinExempt &&
    value !== undefined &&
    value > limits.liftgate.maxDimensionInches
  ) {
    return (
      <>
        Too big for lift gate{" "}
        <Tooltip
          variant="error"
          content={
            <Stack gap={2}>
              <T weight="medium">Too big for lift gate</T>
              <T>
                Freight with dimensions over{" "}
                {limits.liftgate.maxDimensionInches} inches may be too large for
                some carrier&apos;s lift gates to handle.
              </T>
            </Stack>
          }
        />
      </>
    );
  }

  return undefined;
}

export function errorMessageForHeight(
  lineItem: AddLineItemType,
  pickupLocation: AddLocationLocationType,
  deliveryLocation: AddLocationLocationType,
  limits: ShipmentLimits,
  isLongThinExempt: boolean,
): ErrorMessageType {
  const value = lineItem.height;
  if (value === undefined) {
    return "Required";
  }

  const errorMessage = errorMessageForSingleDimension(
    value,
    "tall",
    pickupLocation,
    deliveryLocation,
    limits,
    isLongThinExempt,
  );

  if (errorMessage !== undefined) {
    return errorMessage;
  }

  const heightCapError = errorMessageForHeightValue(value, limits);
  if (heightCapError !== undefined) {
    return heightCapError;
  }

  if (
    lineItem.width !== undefined &&
    lineItem.length !== undefined &&
    lineItem.height !== undefined &&
    lineItem.numberHandlingUnits !== undefined
  ) {
    const totalVolumeCubicInches =
      lineItem.width *
      lineItem.length *
      lineItem.height *
      lineItem.numberHandlingUnits;
    if (totalVolumeCubicInches > limits.shipment.maxTotalVolumeCubicInches) {
      // The actual error message is on length
      return <span>{"\u00A0"}</span>;
    }

    if (totalVolumeCubicInches < limits.lineItem.minTotalVolumeCubicInches) {
      // The actual error message is on length
      return <span>{"\u00A0"}</span>;
    }
  }

  return undefined;
}

// Flatbed envelope check. A line item only fails when both length AND width exceed their
// flatbed triggers (i.e. it can't fit on a standard trailer).
export function isTooLarge(
  width: number,
  length: number,
  limits: ShipmentLimits,
) {
  return (
    length > limits.flatbed.lengthTriggerInches &&
    width > limits.flatbed.widthTriggerInches
  );
}

export function errorMessageForWidth(
  lineItem: AddLineItemType,
  pickupLocation: AddLocationLocationType,
  deliveryLocation: AddLocationLocationType,
  limits: ShipmentLimits,
  isLongThinExempt: boolean,
): ErrorMessageType {
  const widthValue = lineItem.width;
  const lengthValue = lineItem.length;
  if (widthValue === undefined) {
    return "Required";
  }

  const errorMessage = errorMessageForSingleDimension(
    widthValue,
    "wide",
    pickupLocation,
    deliveryLocation,
    limits,
    isLongThinExempt,
  );

  if (errorMessage !== undefined) {
    return errorMessage;
  }

  if (lengthValue !== undefined) {
    if (isTooLarge(widthValue, lengthValue, limits)) {
      // The actual error message is on length
      // This is to pad the space
      return <span>{"\u00A0"}</span>;
    }
  }

  if (
    lineItem.width !== undefined &&
    lineItem.length !== undefined &&
    lineItem.height !== undefined &&
    lineItem.numberHandlingUnits !== undefined
  ) {
    const totalVolumeCubicInches =
      lineItem.width *
      lineItem.length *
      lineItem.height *
      lineItem.numberHandlingUnits;
    if (totalVolumeCubicInches > limits.shipment.maxTotalVolumeCubicInches) {
      // The actual error message is on length
      return <span>{"\u00A0"}</span>;
    }

    if (totalVolumeCubicInches < limits.lineItem.minTotalVolumeCubicInches) {
      // The actual error message is on length
      return <span>{"\u00A0"}</span>;
    }
  }

  return undefined;
}

export function errorMessageForLength(
  lineItem: AddLineItemType,
  pickupLocation: AddLocationLocationType,
  deliveryLocation: AddLocationLocationType,
  limits: ShipmentLimits,
  isLongThinExempt: boolean,
): ErrorMessageType {
  if (lineItem.length === undefined) {
    return "Required";
  }

  const errorMessage = errorMessageForSingleDimension(
    lineItem.length,
    "long",
    pickupLocation,
    deliveryLocation,
    limits,
    isLongThinExempt,
  );

  if (errorMessage !== undefined) {
    return errorMessage;
  }

  if (lineItem.width !== undefined) {
    if (isTooLarge(lineItem.width, lineItem.length, limits)) {
      return "Too large";
    }
  }

  if (
    lineItem.width !== undefined &&
    lineItem.length !== undefined &&
    lineItem.height !== undefined &&
    lineItem.numberHandlingUnits !== undefined
  ) {
    const totalVolumeCubicInches =
      lineItem.width *
      lineItem.length *
      lineItem.height *
      lineItem.numberHandlingUnits;
    if (totalVolumeCubicInches > limits.shipment.maxTotalVolumeCubicInches) {
      return "Too large";
    }

    if (totalVolumeCubicInches < limits.lineItem.minTotalVolumeCubicInches) {
      return "Too small";
    }
  }

  return undefined;
}
